import React, {Suspense, useState} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import clsx from 'clsx';
import {diApi} from '@di-shared/api/di';
import {ERROR, UNIT} from '@di-shared/const';
import DailyLineGraphError from '@di-shared/features/common/DailyLineGraphError';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import environmentStore from '@di-shared/features/environment/environmentStore';
import DailyBarThinGraph, {
  createDailyBarThinGraphYAxisFromScores,
  DailyBarThinGraphYAxis,
} from '@di-shared/features/history/energy-usage/DailyBarThinGraph';
import {EnergyUsageDailyProps} from '@di-shared/features/history/energy-usage/EnergyUsageDaily/index';
import EnergyUsageTooltip from '@di-shared/features/history/energy-usage/EnergyUsageTooltip';
import {EV_DAILY_GRAPH_MAX_LENGTH, useEnergyUsageDaily} from '@di-shared/features/history/energy-usage/useEnergyUsageDaily';
import {roundToFirstDecimal} from '@di-shared/features/history/energy-usage/utils';
import {formatNumberWithCommas} from '@di-shared/utils';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import useUnitConverter from '@di-shared/utils/useUnitConverter';
import {subDate} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import {useSuspenseQuery} from '@tanstack/react-query';

export default function ({vin}: EnergyUsageDailyProps) {
  return (
    <ErrorBoundary fallback={<FallbackError />}>
      <Suspense fallback={<FallbackLoading />}>
        <Inner vin={vin} />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin}: EnergyUsageDailyProps) {
  const ccId = environmentStore.getState().ccid;
  const {data: evDailyData} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.driveHistory.getMonthlyEvDriveStats), vin, ccId],
    queryFn: () => diApi.driveHistory.getMonthlyEvDriveStats({vin, ccId}),
    select: ({data}) => data,
  });
  const {isDailyEnergyUsageNone} = useEnergyUsageDaily({evDailyData});
  // No data 화면
  if (isDailyEnergyUsageNone) {
    return <None />;
  }

  // Default 화면
  return <Default vin={vin} />;
}

interface DailyBarChartDataItem {
  score: number;
  scoreDate: Date;
}

interface EnergyDailyBarChartProps {
  data: Array<DailyBarChartDataItem>;
  error?: boolean;
}

function EnergyPowerDailyGraph({data, error = false}: EnergyDailyBarChartProps) {
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const yAxis = createDailyBarThinGraphYAxisFromScores(data.map(({score}) => score));
  const errorYAxis: DailyBarThinGraphYAxis = {maxValue: 10, guideValues: [5, 10]};

  return (
    <DailyBarThinGraph
      data={data}
      unit={UNIT.KWH}
      yAxis={error ? errorYAxis : yAxis}
      getTooltipData={(selectedDataIndex) => {
        const selectedData = data[selectedDataIndex];

        return {
          time: CldrDatetimeFormatter.format(selectedData.scoreDate, 'MMMd'),
          value: error ? `${ERROR.DASH}${UNIT.KWH}` : `${formatNumberWithCommas(roundToFirstDecimal(selectedData.score))}${UNIT.KWH}`,
        };
      }}
      formatXAxisValue={(scoreDate) => CldrDatetimeFormatter.format(scoreDate, 'short')}
      formatYAxisValue={(value) => formatNumberWithCommas(value)}
    />
  );
}

function EnergyMileageDailyGraph({data, error = false}: EnergyDailyBarChartProps) {
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {defaultEnergyEfficiencySymbol, convertEnergyEfficiencySymbol, convertEnergyEfficiencyValue} = useUnitConverter();

  function getConvertedScore(score: number) {
    const convertedScore = convertEnergyEfficiencyValue(score, {skipFormatting: true});

    return typeof convertedScore === 'number' && Number.isFinite(convertedScore) ? convertedScore : 0;
  }

  const convertedData = data.map(({score, ...others}) => ({
    score: getConvertedScore(score),
    ...others,
  }));
  const yAxis = createDailyBarThinGraphYAxisFromScores(convertedData.map(({score}) => score));
  const errorYAxis: DailyBarThinGraphYAxis = {maxValue: 10, guideValues: [5, 10]};
  return (
    <DailyBarThinGraph
      data={convertedData}
      unit={defaultEnergyEfficiencySymbol}
      yAxis={error ? errorYAxis : yAxis}
      getTooltipData={(selectedDataIndex) => {
        const selectedData = data[selectedDataIndex];
        const convertedSelectedData = convertedData[selectedDataIndex];

        return {
          time: CldrDatetimeFormatter.format(selectedData.scoreDate, 'MMMd'),
          value: error
            ? `${ERROR.DASH}${defaultEnergyEfficiencySymbol}`
            : `${formatNumberWithCommas(roundToFirstDecimal(convertedSelectedData.score))}${convertEnergyEfficiencySymbol(selectedData.score, {skipFormatting: true})}`,
        };
      }}
      formatXAxisValue={(scoreDate) => CldrDatetimeFormatter.format(scoreDate, 'short')}
      formatYAxisValue={(value) => formatNumberWithCommas(value)}
    />
  );
}

interface DailyGraphProps {
  containerClassName?: string;
  energyUseValue: string | number;
  energyEfficiencyValue: string | number;
  energyEfficiencyUnit: string;
  energyUseGraph: React.ReactNode;
  energyUseMaxValue: string | number;
  energyUseMinValue: string | number;
  energyEfficiencyGraph: React.ReactNode;
  energyEfficiencyMaxValue: string | number;
  energyEfficiencyMinValue: string | number;
  energyEfficiencyMaxUnit: string;
  energyEfficiencyMinUnit: string;
}

function DailyGraph({
  containerClassName,
  energyUseValue,
  energyEfficiencyValue,
  energyEfficiencyUnit,
  energyUseGraph,
  energyUseMaxValue,
  energyUseMinValue,
  energyEfficiencyGraph,
  energyEfficiencyMaxValue,
  energyEfficiencyMinValue,
  energyEfficiencyMaxUnit,
  energyEfficiencyMinUnit,
}: DailyGraphProps) {
  const {t} = useInferableTranslation();
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const powerConsumptionLabel = t('T10071');
  const powerEfficiencyLabel = t('T10037');
  const maxLabel = t('T10075');
  const minLabel = t('T10076');
  const isEnergyUseSelected = selectedTabIndex === 0;
  const isEnergyEfficiencySelected = selectedTabIndex === 1;

  return (
    <div className={clsx('section_container', containerClassName)}>
      <div className="section_container_header">
        <div className="device_info_item">
          <h4 className="device_info_key">{powerConsumptionLabel}</h4>
          <span className="device_info_val">
            <span className="device_info_amount">
              <span className="device_info_number">{energyUseValue}</span>
              <span className="device_info_unit">{UNIT.KWH}</span>
            </span>
          </span>
        </div>
        <div className="device_info_item">
          <h4 className="device_info_key">{powerEfficiencyLabel}</h4>
          <span className="device_info_val">
            <span className="device_info_amount">
              <span className="device_info_number">{energyEfficiencyValue}</span>
              <span className="device_info_unit">{energyEfficiencyUnit}</span>
            </span>
          </span>
        </div>
      </div>

      <div
        className="tabbar type_brick"
        role="tablist"
      >
        <button
          type="button"
          className={clsx('tabbar_item body_medium_b', {
            'is-selected': isEnergyUseSelected,
          })}
          onClick={() => setSelectedTabIndex(0)}
          role="tab"
          aria-selected={isEnergyUseSelected}
          tabIndex={isEnergyUseSelected ? 0 : -1}
        >
          {powerConsumptionLabel}
        </button>
        <button
          type="button"
          className={clsx('tabbar_item body_medium_b', {
            'is-selected': isEnergyEfficiencySelected,
          })}
          onClick={() => setSelectedTabIndex(1)}
          role="tab"
          aria-selected={isEnergyEfficiencySelected}
          tabIndex={isEnergyEfficiencySelected ? 0 : -1}
        >
          {powerEfficiencyLabel}
        </button>
      </div>

      <div
        className="tab_content"
        role="tabpanel"
        hidden={!isEnergyUseSelected}
      >
        {isEnergyUseSelected && (
          <>
            <span className="blind">{powerConsumptionLabel}</span>
            {energyUseGraph}
            <ul className="statistics_info_list">
              <li className="statistics_info_item">
                <span className="statistics_info_key">{maxLabel}</span>
                <span className="statistics_info_val">
                  <span className="statistics_info_number">{energyUseMaxValue}</span>
                  <span className="statistics_info_unit">{UNIT.KWH}</span>
                </span>
              </li>
              <li className="statistics_info_item">
                <span className="statistics_info_key">{minLabel}</span>
                <span className="statistics_info_val">
                  <span className="statistics_info_number">{energyUseMinValue}</span>
                  <span className="statistics_info_unit">{UNIT.KWH}</span>
                </span>
              </li>
            </ul>
          </>
        )}
      </div>

      <div
        className="tab_content"
        role="tabpanel"
        hidden={!isEnergyEfficiencySelected}
      >
        {isEnergyEfficiencySelected && (
          <>
            <span className="blind">{powerEfficiencyLabel}</span>
            {energyEfficiencyGraph}
            <ul className="statistics_info_list">
              <li className="statistics_info_item">
                <span className="statistics_info_key">{maxLabel}</span>
                <span className="statistics_info_val">
                  <span className="statistics_info_number">{energyEfficiencyMaxValue}</span>
                  <span className="statistics_info_unit">{energyEfficiencyMaxUnit}</span>
                </span>
              </li>
              <li className="statistics_info_item">
                <span className="statistics_info_key">{minLabel}</span>
                <span className="statistics_info_val">
                  <span className="statistics_info_number">{energyEfficiencyMinValue}</span>
                  <span className="statistics_info_unit">{energyEfficiencyMinUnit}</span>
                </span>
              </li>
            </ul>
          </>
        )}
      </div>
    </div>
  );
}

function None() {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {defaultEnergyEfficiencySymbol} = useUnitConverter();
  // No data 세팅

  const evDrivingInfoNone = Array.from({length: EV_DAILY_GRAPH_MAX_LENGTH})
    .map((_, index) => {
      return {
        date: subDate(new Date(), {days: index + 1}),
        powerConsumption: 0,
        avrMileage: 0,
        regenPower: 0,
      };
    })
    .sort((a, b) => +a.date - +b.date);

  return (
    <section className="section">
      <div className="section_heading type_main">
        <h3
          className="section_heading_title"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T10184')}
        </h3>
        <span className="section_heading_caption">
          {t('T10004_$date', {
            date: `${CldrDatetimeFormatter.format(evDrivingInfoNone[0].date, 'yMMMd')} ~ ${CldrDatetimeFormatter.format(evDrivingInfoNone[evDrivingInfoNone.length - 1].date, 'yMMMd')}`,
          })}
        </span>
      </div>
      <DailyGraph
        energyUseValue={0}
        energyEfficiencyValue={0}
        energyEfficiencyUnit={defaultEnergyEfficiencySymbol}
        energyUseGraph={
          <ErrorBoundary fallback={<DailyLineGraphError />}>
            <EnergyPowerDailyGraph
              data={evDrivingInfoNone.map((item) => ({
                score: item.powerConsumption,
                scoreDate: item.date,
              }))}
            />
          </ErrorBoundary>
        }
        energyUseMaxValue={0}
        energyUseMinValue={0}
        energyEfficiencyGraph={
          <ErrorBoundary fallback={<DailyLineGraphError />}>
            <EnergyMileageDailyGraph
              data={evDrivingInfoNone.map((item) => ({
                score: item.avrMileage,
                scoreDate: item.date,
              }))}
            />
          </ErrorBoundary>
        }
        energyEfficiencyMaxValue={0}
        energyEfficiencyMinValue={0}
        energyEfficiencyMaxUnit={defaultEnergyEfficiencySymbol}
        energyEfficiencyMinUnit={defaultEnergyEfficiencySymbol}
      />
      <div className="section_container section_container_small">
        <div className="device_info_item">
          <span className="device_info_icon">
            <span className="ic ic_reservation_charge_24px" />
          </span>
          <div className="device_info_text">
            <h4 className="device_info_key">{t('T10072')}</h4>
            <span className="device_info_val">
              <span className="device_info_amount">
                <span className="device_info_number">{0}</span>
                <span className="device_info_unit">{UNIT.KWH}</span>
              </span>
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

function Default({vin}: EnergyUsageDailyProps) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const ccId = environmentStore.getState().ccid;
  const {data: evDailyData} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.driveHistory.getMonthlyEvDriveStats), vin, ccId],
    queryFn: () => diApi.driveHistory.getMonthlyEvDriveStats({vin, ccId}),
    select: ({data}) => data,
  });
  const {verifiedEvDrivingInfoList, minPowerConsumption, maxPowerConsumption, maxAvrMileage, minAvrMileage} = useEnergyUsageDaily({
    evDailyData,
  });

  const {convertEnergyEfficiencyValue, convertEnergyEfficiencySymbol} = useUnitConverter();

  function roundDisplayValue(value: number | string) {
    return typeof value === 'number' ? roundToFirstDecimal(value) : value;
  }

  return (
    <section className="section">
      <div className="section_heading type_main">
        <h3
          className="section_heading_title"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T10073')}
          <EnergyUsageTooltip />
        </h3>
        <span className="section_heading_caption">
          {t('T10004_$date', {
            date: `${CldrDatetimeFormatter.format(verifiedEvDrivingInfoList[0].verifiedDate, 'yMMMd')} ~ ${CldrDatetimeFormatter.format(verifiedEvDrivingInfoList[verifiedEvDrivingInfoList.length - 1].verifiedDate, 'yMMMd')}`,
          })}
        </span>
      </div>
      <DailyGraph
        energyUseValue={formatNumberWithCommas(roundDisplayValue(evDailyData.evStatistics.totalPowerConsumption))}
        energyEfficiencyValue={formatNumberWithCommas(
          roundDisplayValue(convertEnergyEfficiencyValue(evDailyData.evStatistics.avrMileage, {skipFormatting: true}))
        )}
        energyEfficiencyUnit={convertEnergyEfficiencySymbol(evDailyData.evStatistics.avrMileage, {skipFormatting: true})}
        energyUseGraph={
          <ErrorBoundary fallback={<DailyLineGraphError />}>
            <EnergyPowerDailyGraph
              data={verifiedEvDrivingInfoList.map((item) => ({
                score: roundToFirstDecimal(item.powerConsumption),
                scoreDate: item.verifiedDate,
              }))}
            />
          </ErrorBoundary>
        }
        energyUseMaxValue={formatNumberWithCommas(roundDisplayValue(maxPowerConsumption))}
        energyUseMinValue={formatNumberWithCommas(roundDisplayValue(minPowerConsumption))}
        energyEfficiencyGraph={
          <ErrorBoundary fallback={<DailyLineGraphError />}>
            <EnergyMileageDailyGraph
              data={verifiedEvDrivingInfoList.map((item) => ({
                score: item.avrMileage,
                scoreDate: item.verifiedDate,
              }))}
            />
          </ErrorBoundary>
        }
        energyEfficiencyMaxValue={formatNumberWithCommas(
          roundDisplayValue(convertEnergyEfficiencyValue(maxAvrMileage, {skipFormatting: true}))
        )}
        energyEfficiencyMinValue={formatNumberWithCommas(
          roundDisplayValue(convertEnergyEfficiencyValue(minAvrMileage, {skipFormatting: true}))
        )}
        energyEfficiencyMaxUnit={convertEnergyEfficiencySymbol(maxAvrMileage, {skipFormatting: true})}
        energyEfficiencyMinUnit={convertEnergyEfficiencySymbol(minAvrMileage, {skipFormatting: true})}
      />
      <div className="section_container section_container_small">
        <div className="device_info_item">
          <span className="device_info_icon">
            <span className="ic ic_reservation_charge_24px" />
          </span>
          <div className="device_info_text">
            <h4 className="device_info_key">{t('T10072')}</h4>
            <span className="device_info_val">
              <span className="device_info_amount">
                <span className="device_info_number">
                  {formatNumberWithCommas(roundDisplayValue(evDailyData.evStatistics.totalRegenPower))}
                </span>
                <span className="device_info_unit">{UNIT.KWH}</span>
              </span>
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

function FallbackLoading() {
  const {t} = useInferableTranslation();
  const {defaultEnergyEfficiencySymbol} = useUnitConverter();
  return (
    <section className="section">
      <div className="section_heading type_main">
        <h3
          className="section_heading_title skeleton"
          style={{width: '160px', height: '20px'}}
        />
        <h3
          className="section_heading_title skeleton"
          style={{width: '200px', height: '28px'}}
        />
        <span
          className="section_heading_caption skeleton"
          style={{width: '160px', height: '20px'}}
        />
      </div>
      <DailyGraph
        containerClassName="skeleton"
        energyUseValue={ERROR.DASH}
        energyEfficiencyValue={ERROR.DASH}
        energyEfficiencyUnit={defaultEnergyEfficiencySymbol}
        energyUseGraph={<DailyLineGraphError />}
        energyUseMaxValue={ERROR.DASH}
        energyUseMinValue={ERROR.DASH}
        energyEfficiencyGraph={<DailyLineGraphError />}
        energyEfficiencyMaxValue={ERROR.DASH}
        energyEfficiencyMinValue={ERROR.DASH}
        energyEfficiencyMaxUnit={defaultEnergyEfficiencySymbol}
        energyEfficiencyMinUnit={defaultEnergyEfficiencySymbol}
      />
      <div className="section_container section_container_small skeleton">
        <div className="device_info_item">
          <span className="device_info_icon">
            <span className="ic ic_reservation_charge_24px" />
          </span>
          <div className="device_info_text">
            <h4 className="device_info_key">{t('T10072')}</h4>
            <span className="device_info_val">
              <span className="device_info_amount">
                <span className="device_info_number">{ERROR.DASH}</span>
                <span className="device_info_unit">{UNIT.KWH}</span>
              </span>
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

function FallbackError() {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {defaultEnergyEfficiencySymbol} = useUnitConverter();
  // No data 세팅
  const evDrivingInfoNone = Array.from({length: EV_DAILY_GRAPH_MAX_LENGTH})
    .map((_, index) => {
      return {
        date: subDate(new Date(), {days: index + 1}),
        powerConsumption: 0,
        avrMileage: 0,
        regenPower: 0,
      };
    })
    .sort((a, b) => +a.date - +b.date);

  return (
    <section className="section">
      <div className="section_heading type_main">
        <h3
          className="section_heading_title"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T10073')}
          <EnergyUsageTooltip />
        </h3>
        <span className="section_heading_caption">
          {t('T10004_$date', {
            date: `${CldrDatetimeFormatter.format(evDrivingInfoNone[0].date, 'yMMMd')} ~ ${CldrDatetimeFormatter.format(evDrivingInfoNone[evDrivingInfoNone.length - 1].date, 'yMMMd')}`,
          })}
        </span>
      </div>
      <DailyGraph
        energyUseValue={ERROR.DASH}
        energyEfficiencyValue={ERROR.DASH}
        energyEfficiencyUnit={defaultEnergyEfficiencySymbol}
        energyUseGraph={
          <ErrorBoundary fallback={<DailyLineGraphError />}>
            <EnergyPowerDailyGraph
              data={evDrivingInfoNone.map((item) => ({
                score: item.powerConsumption,
                scoreDate: item.date,
              }))}
              error={true}
            />
          </ErrorBoundary>
        }
        energyUseMaxValue={ERROR.DASH}
        energyUseMinValue={ERROR.DASH}
        energyEfficiencyGraph={
          <ErrorBoundary fallback={<DailyLineGraphError />}>
            <EnergyMileageDailyGraph
              data={evDrivingInfoNone.map((item) => ({
                score: item.avrMileage,
                scoreDate: item.date,
              }))}
              error={true}
            />
          </ErrorBoundary>
        }
        energyEfficiencyMaxValue={ERROR.DASH}
        energyEfficiencyMinValue={ERROR.DASH}
        energyEfficiencyMaxUnit={defaultEnergyEfficiencySymbol}
        energyEfficiencyMinUnit={defaultEnergyEfficiencySymbol}
      />
      <div className="section_container section_container_small">
        <div className="device_info_item">
          <span className="device_info_icon">
            <span className="ic ic_reservation_charge_24px" />
          </span>
          <div className="device_info_text">
            <h4 className="device_info_key">{t('T10072')}</h4>
            <span className="device_info_val">
              <span className="device_info_amount">
                <span className="device_info_number">{ERROR.DASH}</span>
                <span className="device_info_unit">{UNIT.KWH}</span>
              </span>
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
